import { useTranslation } from 'react-i18next';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AddressForm } from './../NewAddressBox';
import {
  AddressButton,
  PrimaryButton,
} from '../../../../../components/Buttons';
import { IAddress } from '../../../../../lib/interfaces';
import { FormProvider, useForm } from 'react-hook-form';
import { useAlert } from '../../../../../providers/AlertProvider';

interface IEditAddress {
  loading: boolean;
  address: IAddress;
  onAddressEdit: (_: IAddress) => void;
}

const EditAddress = (props: IEditAddress) => {
  const {
    loading = false,
    address,
    onAddressEdit = (_: IAddress) => {},
  } = props;
  const { t } = useTranslation('PROFILE');
  const [open, setOpen] = useState(false);
  const [, dispatchAlertChange] = useAlert();
  const cancelButtonRef = useRef(null);
  const formMethods = useForm<IAddress>({
    defaultValues: address,
  });
  const { handleSubmit, reset: resetForm } = formMethods;

  useEffect(() => {
    address && resetForm(address);
  }, [address]);

  return (
    <>
      <AddressButton text={t('COMMON:EDIT')} onClick={() => setOpen(true)} />

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
            setTimeout(resetForm, 500);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <div>
                  <Dialog.Title
                    as="h3"
                    className="leading-6 text-md font-bold text-gray-800"
                  >
                    {t('MODIFY_ADDRESS')}
                  </Dialog.Title>
                  <FormProvider {...formMethods}>
                    <AddressForm />
                  </FormProvider>
                </div>

                <div className="mt-6 flex justify-end">
                  <PrimaryButton
                    text={t('COMMON:SAVE_CHANGES')}
                    loading={loading}
                    onClick={handleSubmit(
                      (formData) => {
                        onAddressEdit(formData);
                        setOpen(false);
                      },
                      () => {
                        dispatchAlertChange({
                          open: true,
                          message: t('VALIDATION_ERROR'),
                        });
                      }
                    )}
                  />
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default EditAddress;
